import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Film, Image as ImageIcon, Layers, Loader2, Search } from "lucide-react";
import useRequest from "../../../../components/hook/use-req";
import { CTA_TOKEN_OPTIONS, extractCreativeContent, useAdCreatives } from "./api";
import { CreativeFormat, CreativeState, FORMAT_LABEL } from "./types";
import { Field, SelectBox, StepFooter, inputCls } from "./ui";

const MAX_BYTES = 16 * 1024 * 1024;

const FORMAT_TABS: { id: CreativeFormat; icon: React.ReactNode }[] = [
  { id: "single", icon: <ImageIcon size={20} /> },
  { id: "carousel", icon: <Layers size={20} /> },
  { id: "reel", icon: <Film size={20} /> },
];

const ACCEPT: Record<CreativeFormat, string> = {
  single: "image/jpeg,image/png",
  carousel: "image/jpeg,image/png,video/mp4",
  reel: "video/mp4",
};

const StepMessaging: React.FC<{
  broadcastName: string;
  creative: CreativeState;
  updateCreative: (patch: Partial<CreativeState>) => void;
  senderId: string;
  onBack: () => void;
  onNext: () => void;
  onSaveDraft: () => void;
  savingDraft: boolean;
  nextDisabled?: boolean;
}> = ({ broadcastName, creative, updateCreative, senderId, onBack, onNext, onSaveDraft, savingDraft, nextDisabled }) => {
  const [search, setSearch] = useState("");
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const token = localStorage.getItem("authToken");
  const { makeRequest: uploadMedia } = useRequest("/media/upload", "POST", { Authorization: `Bearer ${token}` });

  // Real existing ad creatives from the org's own Meta ad account
  // (GET /instagram/ads/creatives) — offered as starting points instead of a
  // hardcoded sample list; picking one prefills the editor on the right but
  // never auto-submits anything.
  const { items: adCreatives, loading: creativesLoading } = useAdCreatives(senderId);
  const templates = adCreatives.filter((c) => {
    const content = extractCreativeContent(c);
    return (c.name || "").toLowerCase().includes(search.toLowerCase()) || (content.primaryText || "").toLowerCase().includes(search.toLowerCase());
  });

  // Meta needs a real hosted URL for media_url/thumbnail_url, so the file is
  // uploaded straight away (same /media/upload endpoint the ticket attachment
  // flow uses) rather than only kept as a local blob preview.
  const handleFile = async (file?: File | null) => {
    if (!file) return;
    const kind = file.type.startsWith("video/") ? "video" : file.type.startsWith("image/") ? "image" : null;
    if (!kind) {
      toast.error("Only JPG, PNG or MP4 files are supported.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("File is larger than 16MB.");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("files", file);
      const [response, status] = await uploadMedia(formData);
      const url = response?.uploaded_files?.[0]?.preview_url;
      if (status >= 200 && status < 300 && url) {
        updateCreative({ media: { url, kind, name: file.name } });
      } else {
        toast.error("Failed to upload the file — try again.");
      }
    } catch {
      toast.error("Failed to upload the file — try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {/* <SectionHeader
        title="Broadcast Creative and Placement"
        subtitle="Add your media, copy and destination"
        action={<AddButton label="Create New Template" onClick={() => toast.info("Template creation isn't available here yet.")} />}
      /> */}

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,260px)_minmax(0,1fr)] gap-5">
        {/* templates */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden self-start">
          <div className="p-2.5 bg-gray-50 dark:bg-gray-800">
            <div className="relative">
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ad creatives" className={`${inputCls} pr-10`} />
              <Search size={20} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-800 dark:text-gray-200" />
            </div>
          </div>
          <div className="max-h-[820px] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
            {!senderId ? (
              <p className="text-sm text-gray-400 px-4 py-6 text-center">Pick a Sender Profile in Broadcast Details to see your existing ad creatives.</p>
            ) : creativesLoading ? (
              <p className="text-sm text-gray-400 px-4 py-6 text-center flex items-center justify-center gap-2">
                <Loader2 size={16} className="animate-spin" /> Loading ad creatives…
              </p>
            ) : (
              templates.map((t) => {
                const content = extractCreativeContent(t);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() =>
                      updateCreative({
                        templateId: t.id,
                        primaryText: content.primaryText || creative.primaryText,
                        headline: content.headline || creative.headline,
                        cta: content.cta || creative.cta,
                        media: content.mediaUrl ? { url: content.mediaUrl, kind: "image", name: t.name || "Ad creative" } : creative.media,
                      })
                    }
                    className={`w-full text-left px-4 py-3 transition-colors ${creative.templateId === t.id ? "bg-rose-50 dark:bg-rose-500/10" : "hover:bg-gray-50 dark:hover:bg-gray-800"}`}
                  >
                    <span className="text-lg text-gray-900 dark:text-gray-100">{t.name || "Untitled creative"}</span>
                    <span className="block text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">{content.primaryText || "No preview text"}</span>
                  </button>
                );
              })
            )}
            {senderId && !creativesLoading && templates.length === 0 && (
              <p className="text-sm text-gray-400 px-4 py-6 text-center">No existing ad creatives found — build a new one below.</p>
            )}
          </div>
        </div>

        {/* editor */}
        <div>
          <p className="text-lg text-gray-900 dark:text-gray-100 mb-3">{broadcastName || "Untitled broadcast"}</p>
          <div className="rounded-lg bg-primary-50 dark:bg-primary-500/10 p-2.5">
            <div className="grid grid-cols-3 gap-1 mb-2.5">
              {FORMAT_TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => updateCreative({ format: t.id })}
                  className={`flex items-center justify-center gap-1.5 py-3.5 px-1 whitespace-nowrap rounded-md text-base transition-colors ${
                    creative.format === t.id ? "bg-white dark:bg-gray-900 text-primary-700 shadow-sm" : "text-gray-800 dark:text-gray-200"
                  }`}
                >
                  {t.icon} {FORMAT_LABEL[t.id]}
                </button>
              ))}
            </div>

            <div className="rounded-lg bg-white dark:bg-gray-900 p-4 space-y-5">
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  void handleFile(e.dataTransfer.files?.[0]);
                }}
                className={`rounded-md border border-dashed px-4 py-7 text-center ${dragging ? "border-primary-700 bg-primary-50/50" : "border-gray-500 dark:border-gray-500"}`}
              >
                {uploading ? (
                  <p className="text-sm text-gray-600 dark:text-gray-300 flex items-center justify-center gap-2">
                    <Loader2 size={16} className="animate-spin" /> Uploading…
                  </p>
                ) : creative.media ? (
                  <>
                    <p className="text-sm text-teal-800 dark:text-teal-400 break-all">{creative.media.name}</p>
                    <div className="mt-3 flex items-center justify-center gap-2">
                      <button type="button" onClick={() => fileRef.current?.click()} className="px-4 py-2 text-base bg-gray-50 dark:bg-gray-800 rounded-md text-gray-900 dark:text-gray-100">
                        Replace
                      </button>
                      <button type="button" onClick={() => updateCreative({ media: null })} className="px-4 py-2 text-base bg-gray-50 dark:bg-gray-800 rounded-md text-rose-600">
                        Remove
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-teal-800 dark:text-teal-400">Add Image or Video</p>
                    <button type="button" onClick={() => fileRef.current?.click()} className="mt-3 px-4 py-2 text-base bg-gray-50 dark:bg-gray-800 rounded-md text-gray-900 dark:text-gray-100">
                      Browse Files
                    </button>
                  </>
                )}
                <p className="mt-4 text-sm text-gray-700 dark:text-gray-300 flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
                  Supports JPG, PNG, MP4 <span className="w-1.5 h-1.5 rounded-full bg-teal-800" /> max 16MB
                </p>
                <input
                  ref={fileRef}
                  type="file"
                  accept={ACCEPT[creative.format]}
                  className="hidden"
                  onChange={(e) => {
                    void handleFile(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
              </div>

              <Field label="Primary Text">
                <input value={creative.primaryText} onChange={(e) => updateCreative({ primaryText: e.target.value })} placeholder="e.g. A Christmas Sale" className={inputCls} />
              </Field>
              <Field label="Headline">
                <input value={creative.headline} onChange={(e) => updateCreative({ headline: e.target.value })} placeholder="e.g. A Christmas Sale" className={inputCls} />
              </Field>
              <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4">
                <Field label="Call to action">
                  <SelectBox
                    value={CTA_TOKEN_OPTIONS.find((c) => c.value === creative.cta)?.label || ""}
                    onChange={(label) => {
                      const hit = CTA_TOKEN_OPTIONS.find((c) => c.label === label);
                      if (hit) updateCreative({ cta: hit.value });
                    }}
                    options={CTA_TOKEN_OPTIONS.map((c) => c.label)}
                    placeholder="Select a call to action"
                  />
                </Field>
                <Field label="Destination URL">
                  <input value={creative.destinationUrl} onChange={(e) => updateCreative({ destinationUrl: e.target.value })} placeholder="e.g. 15" className={inputCls} />
                </Field>
              </div>
            </div>
          </div>

          <StepFooter onBack={onBack} onNext={onNext} nextLabel="Setup Budget" onSaveDraft={onSaveDraft} savingDraft={savingDraft} nextDisabled={nextDisabled || uploading} />
        </div>
      </div>
    </div>
  );
};

export default StepMessaging;
